import type { ReportPreviewPayload } from 'api/analytics-reports/types';
import type {
  ContentListResponse,
  ContentPost,
  MetricComparison,
  PerformanceResponse,
} from 'api/new-analytics/types';

const PERIOD = {
  since: '2026-05-01',
  until: '2026-05-21',
  timezone: 'Europe/Bratislava',
} as const;

function metric(value: number, changePercent: number | null): MetricComparison {
  return {
    value,
    previous_value:
      changePercent === null
        ? null
        : Math.round(value / (1 + changePercent / 100)),
    change: null,
    change_percent: changePercent,
    change_direction:
      changePercent === null
        ? null
        : changePercent > 0
        ? 'up'
        : changePercent < 0
        ? 'down'
        : 'flat',
  };
}

const PERFORMANCE_OVERVIEW = {
  reactions_angry: metric(36, 26),
  reactions_like: metric(342, -3),
  reactions_love: metric(39, -6),
  reactions_haha: metric(36, 34),
  reactions_sad: metric(16, 47),
  reactions_wow: metric(105, 34),
  followers: metric(31100, 3.9),
  followers_gained: metric(306, 3.9),
  video_views: metric(36100, 1),
  reach: metric(29800, 4.2),
  views: metric(82100, 14.4),
  engagements: metric(25300, -5.8),
  engagement_rate: metric(8.4, 8.7),
  impressions: metric(11500, 6.7),
  clicks: metric(612, -6.9),
};

const DAYS = Array.from({ length: 14 }, (_, index) => ({
  date: `2026-05-${String(index + 1).padStart(2, '0')}`,
}));

function series(base: number, step: number) {
  return DAYS.map((day, index) => ({
    ...day,
    value: base + index * step,
  }));
}

const TIME_SERIES = {
  followers: series(30000, 120),
  followers_gained: series(80, 9),
  video_views_paid: series(1200, 180),
  video_views_organic: series(900, 60),
};

const PERFORMANCE_DATA: PerformanceResponse = {
  period: PERIOD,
  totals: PERFORMANCE_OVERVIEW,
  pages: [
    {
      page_id: 1,
      page_name: 'Kontentino',
      platform: 'facebook',
      overview: PERFORMANCE_OVERVIEW,
      time_series: TIME_SERIES,
    },
  ],
};

/** Captions deliberately differ in length — the cards have to stay level. */
const MOCK_POSTS: ContentPost[] = [
  ['Story', 3200, 14, 8, 'Short one.'],
  [
    'Reels',
    2800,
    11,
    6,
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur at diam sed odio semper, quis tincidunt arcu congue ut nulla facilisi.',
  ],
  ['Reels', 1900, 9, 4, 'Lorem ipsum dolor sit amet, consectetur adipiscing.'],
].map(([format, views, shares, saved, content], index) => ({
  post_id: index + 1,
  page_id: 1,
  page_name: 'Kontentino',
  platform: 'facebook' as const,
  entity_type: 'post' as const,
  post_format: format as ContentPost['post_format'],
  is_promoted: false,
  published_at: `2026-05-0${index + 1}T10:00:00Z`,
  content: content as string,
  preview: {},
  metrics: {
    views: views as number,
    shares: shares as number,
    saved: saved as number,
  },
}));

const CONTENT_DATA: ContentListResponse = {
  summary: {
    total_posts: 42,
    avg_engagement_rate: 6.1,
    total_reactions: 18400,
    total_likes: 15200,
    total_comments: 2100,
    total_shares: 1100,
    total_reach: 24600,
    total_views: 71300,
    total_engagements: 21600,
    total_saves: 940,
    total_clicks: 530,
    total_video_views: 38800,
    best_post: null,
    worst_post: null,
  },
  posts: MOCK_POSTS,
};

function mockProfile(
  id: number,
  pageName: string,
  platform: 'facebook' | 'instagram',
): ReportPreviewPayload['profiles'][number] {
  return {
    id,
    type: 'profile',
    page_id: id,
    page_name: pageName,
    platform,
    order_column: id,
    sections: [
      {
        id: id * 10,
        kind: 'performance',
        note: null,
        metrics: Object.keys(PERFORMANCE_OVERVIEW).filter(
          (key) => !key.startsWith('reactions_'),
        ),
        data: PERFORMANCE_DATA,
      },
      {
        id: id * 10 + 1,
        kind: 'content',
        note: null,
        metrics: ['engagement_rate', 'engagements', 'reach', 'views'],
        order_by_posts: null,
        order_by_reels: null,
        post_count: null,
        data: CONTENT_DATA,
      },
    ],
  };
}

/**
 * Dev-only fixture behind `?mock=1`, so the landscape layout can be worked on
 * without a generated report. Never reachable in a production build.
 */
export const ANALYTICS_EXPORT_MOCK_PREVIEW: ReportPreviewPayload = {
  report: {
    id: 0,
    name: 'Mock export',
    period: { preset: 'custom', from: PERIOD.since, to: PERIOD.until },
    template_code: 'growth_pulse',
  },
  // Two profiles, so the fixture shows a group: performance sheets for both
  // come before either profile's content sheets.
  profiles: [
    mockProfile(1, 'Kontentino', 'facebook'),
    mockProfile(2, 'Kontentino IG', 'instagram'),
  ],
};
